import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
} from '@angular/core';
import { BackendErrorResponse, RequestError } from '@freelancer/datastore';
import { Margin } from '@freelancer/ui/margin';
import { FontColor, FontWeight } from '@freelancer/ui/text';

const exceptionErrors = ['UNKNOWN', 'UNKNOWN_ERROR'];

@Component({
  selector: 'fl-generic-error',
  template: `
    <fl-bit class="SameLineContainer">
      <fl-text
        i18n="Generic error message title"
        [flMarginRight]="Margin.XXXSMALL"
      >
        Oops, something went wrong.
      </fl-text>
      <fl-text>
        <ng-content></ng-content>
      </fl-text>
      <fl-text
        i18n="Generic error solution suggestions text"
        [flMarginRight]="Margin.XXXSMALL"
      >
        Please
        <fl-link
          flTrackingLabel="LinkToRetryGenericError"
          *ngIf="canRetry || hasSetRetry; else noRetry"
          (click)="handleRetry($event)"
        >
          try again.
        </fl-link>
        <ng-template #noRetry> try again. </ng-template>
      </fl-text>
      <fl-text
        *ngIf="!hasGenericErrorCode || error.requestId; else noErrorInfo"
        i18n="Default redirect to support suggestion"
      >
        If the problem persists, please contact
        <fl-link
          flTrackingLabel="LinkToSupportGenericError"
          [link]="'/support'"
        >
          support
        </fl-link>
        with the following error information:
      </fl-text>
      <ng-template #noErrorInfo>
        <fl-text i18n="Default redirect to support suggestion">
          If the problem persists, please contact
          <fl-link
            flTrackingLabel="LinkToSupportGenericError"
            [link]="'/support'"
          >
            support.
          </fl-link>
        </fl-text>
      </ng-template>
    </fl-bit>
    <fl-bit class="SameLineContainer">
      <fl-text
        i18n="Generic error message error code"
        *ngIf="!hasGenericErrorCode"
        [color]="FontColor.MID"
        [flMarginRight]="Margin.XXSMALL"
      >
        Error Code: {{ error.errorCode }}
      </fl-text>
      <fl-text
        i18n="Generic error message request ID"
        *ngIf="error.requestId"
        [color]="FontColor.MID"
      >
        Request ID: {{ error.requestId }}
      </fl-text>
    </fl-bit>
  `,
  styleUrls: ['./generic-error.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GenericErrorComponent implements OnChanges {
  FontColor = FontColor;
  FontWeight = FontWeight;
  Margin = Margin;

  canRetry = false;
  hasSetRetry = false;

  hasGenericErrorCode = false;

  @Input() error: BackendErrorResponse<any> | RequestError<any>;

  @Output() retry = new EventEmitter<MouseEvent>();

  ngOnChanges() {
    this.canRetry = 'retry' in this.error;
    this.hasSetRetry = this.retry.observers.length > 0;
    this.hasGenericErrorCode = exceptionErrors.some(
      errorCode => errorCode === this.error.errorCode,
    );
  }

  handleRetry(e: MouseEvent) {
    if (this.hasSetRetry) {
      this.retry.emit(e);
    } else if (this.canRetry) {
      (this.error as RequestError<any>).retry();
    }
  }
}
